import {
	createExternalPhotoPicker,
	deleteExternalPhotoPicker,
	ExternalPhotoPickerResult,
	getExternalPhotoPicker,
	getExternalPhotoPickerPhotos,
	PartnerPhoto,
	StoreType,
} from "../../services/storageService";
import { getCookie, writeCookie } from "../../utils/cookie";
import { IDisposable } from "../../utils/disposable";

export class ExternalPhotoPicker {
	// Start util-methods
	// They live in this file for simplicity
	// Use the generic core-methods when moving this file to .net core
	//
	private waitCancellableAsync = async (delayInMs: number, cancellationToken: { cancel: () => void }) => {
		return new Promise((resolve) => {
			let timeoutRef: NodeJS.Timeout | null = setTimeout(resolve, delayInMs);
			cancellationToken.cancel = () => {
				// Only resolve once
				if (timeoutRef == null) return;

				// cancel timeout
				clearTimeout(timeoutRef);
				timeoutRef = null;

				// The timeout is cancelled and won't trigger on its own
				// so we have to manually resolve the Promise.
				return resolve(undefined);
			};
		});
	};
	private expressEventListener<T>(
		element: any,
		type: string,
		listener: (ev: any) => T,
		useCapture?: boolean,
	): IDisposable {
		const disposable = {
			dispose: (): void => {
				if (disposable.handler) {
					// Prevent unregistering twice
					element.removeEventListener(disposable.type, disposable.handler, useCapture);
					delete (disposable as any).handler;
				}
			},
			handler: (ev: any): T => {
				return listener(ev);
			},
			type,
		};

		element.addEventListener(disposable.type, disposable.handler, useCapture);

		return disposable;
	}
	private pageVisibilityChange = (
		onVisible?: () => void | undefined,
		onHidden?: () => void | undefined,
	): IDisposable | null => {
		if (!onVisible && !onHidden) return null;

		let hidden: string, visibilityState: string, visibilityChange: string;
		hidden = "";
		visibilityChange = "";

		if (typeof document.hidden !== "undefined") {
			hidden = "hidden";
			visibilityChange = "visibilitychange";
			visibilityState = "visibilityState";
		}
		// @ts-ignore
		else if (typeof document.mozHidden !== "undefined") {
			hidden = "mozHidden";
			visibilityChange = "mozvisibilitychange";
			visibilityState = "mozVisibilityState";
		}
		// @ts-ignore
		else if (typeof document.msHidden !== "undefined") {
			hidden = "msHidden";
			visibilityChange = "msvisibilitychange";
			visibilityState = "msVisibilityState";
		}
		// @ts-ignore
		else if (typeof document.webkitHidden !== "undefined") {
			hidden = "webkitHidden";
			visibilityChange = "webkitvisibilitychange";
			visibilityState = "webkitVisibilityState";
		}

		if (typeof document.addEventListener === "undefined" || typeof hidden === "undefined")
			// not supported
			return null;

		return this.expressEventListener(
			document,
			visibilityChange,
			() => {
				setTimeout(() => {
					switch (document[visibilityState]) {
						case "visible":
							onVisible && onVisible();
							break;
						case "hidden":
							onHidden && onHidden();
							break;
					}
				}, 0);
			},
			false,
		);
	};
	// End util-methods

	private sessionCookieName = "epp";
	private externalPhotoPickerData: ExternalPhotoPickerResult | null | undefined;
	private pollingCancellationToken = { cancel: Function };
	private busyPolling = false;

	public async createExternalPhotoPicker(maxItemCount = 0) {
		// Close the session from the cookie
		const sessionId = getCookie(this.sessionCookieName);
		if (sessionId && (!this.externalPhotoPickerData || sessionId != this.externalPhotoPickerData.Id))
			await this.deleteExternalPhotoPickerById(sessionId);

		// only 3600 sec left (= 1 hour) => delete and create a new one
		if (this.externalPhotoPickerData && this.externalPhotoPickerData.ExpiresInSec < 3600)
			this.deleteExternalPhotoPicker();

		// only create session if non already exists
		if (!this.externalPhotoPickerData) {
			this.externalPhotoPickerData = await createExternalPhotoPicker(
				StoreType.GooglePhotos,
				getCookie("accid"),
				window.context.pointOfSaleId,
				maxItemCount,
			);

			// Keep track of open session in a cookie so we can do a cleanup if it's left open
			if (this.externalPhotoPickerData)
				this.setSessionCookie(this.externalPhotoPickerData.Id, this.externalPhotoPickerData.ExpiresInSec);
		}
	}

	public openPicker(): Window | null {
		if (!this.externalPhotoPickerData) return null;

		const url = this.externalPhotoPickerData.PickerUri;
		return this.popupCenter({ url, w: 800, h: 600 });
	}

	public startPickerPolling = async () => {
		if (!this.externalPhotoPickerData) return;

		// Already busy polling? Don't start another poll
		if (this.busyPolling) return;

		this.busyPolling = true;

		// on page visibile => cancel the timeout
		const eventHandler = this.pageVisibilityChange(() => {
			this.pollingCancellationToken.cancel();
		});

		let result = true;
		do {
			const waitInMs = this.externalPhotoPickerData.PollingIntervalInSec * 1000;
			await this.waitCancellableAsync(waitInMs, this.pollingCancellationToken);

			this.externalPhotoPickerData = await this.getSession();
			if (!this.externalPhotoPickerData) {
				result = false;
				break;
			}
		} while (!this.externalPhotoPickerData.MediaItemsSet);

		eventHandler?.dispose();

		this.busyPolling = false;

		return result;
	};

	public async getSession(): Promise<ExternalPhotoPickerResult | null> {
		return new Promise((resolve, reject) => {
			if (!this.externalPhotoPickerData) return null;

			getExternalPhotoPicker(
				StoreType.GooglePhotos,
				getCookie("accid"),
				window.context.pointOfSaleId,
				this.externalPhotoPickerData.Id,
			).then(resolve, reject);
		});
	}

	public async getSelectedPhotos(): Promise<PartnerPhoto[]> {
		if (!this.externalPhotoPickerData) return [];

		return await getExternalPhotoPickerPhotos(
			StoreType.GooglePhotos,
			getCookie("accid"),
			window.context.pointOfSaleId,
			this.externalPhotoPickerData.Id,
		);
	}

	public async deleteExternalPhotoPicker(): Promise<boolean> {
		return await this.deleteExternalPhotoPickerById(this.externalPhotoPickerData?.Id || "");
	}

	private async deleteExternalPhotoPickerById(id: string): Promise<boolean> {
		if (!id) return true;

		this.externalPhotoPickerData = null;

		// stop polling
		this.pollingCancellationToken.cancel();

		const result = await deleteExternalPhotoPicker(
			StoreType.GooglePhotos,
			getCookie("accid"),
			window.context.pointOfSaleId,
			id,
		);

		// Whether we failed to delete session or not => delete the cookie anyway to prevent recurring errors
		this.clearSessionCookie();

		return result;
	}

	private popupCenter = ({ url, w, h }) => {
		const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
		const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

		const width = window.innerWidth
			? window.innerWidth
			: document.documentElement.clientWidth
				? document.documentElement.clientWidth
				: screen.width;
		const height = window.innerHeight
			? window.innerHeight
			: document.documentElement.clientHeight
				? document.documentElement.clientHeight
				: screen.height;

		const systemZoom = width / window.screen.availWidth;
		const left = (width - w) / 2 / systemZoom + dualScreenLeft;
		const top = (height - h) / 2 / systemZoom + dualScreenTop;
		return window.open(
			url,
			"_blank",
			`scrollbars=yes,width=${w / systemZoom},height=${h / systemZoom},top=${top},left=${left}`,
		);
	};

	// start session cookie
	//
	// Keep track of open session.
	// Close all open sessions before creating a new one
	//
	private setSessionCookie(id: string, expiresInSeconds: number): void {
		const existingCookieValue = getCookie(this.sessionCookieName);
		if (existingCookieValue) this.deleteExternalPhotoPickerById(existingCookieValue);

		const expiresInDays = expiresInSeconds / 60 / 60 / 24 + 1;
		writeCookie(this.sessionCookieName, id, expiresInDays);
	}
	private clearSessionCookie(): void {
		const existingCookieValue = getCookie(this.sessionCookieName);
		if (!existingCookieValue) return;

		writeCookie(this.sessionCookieName, "", -1);
	}
	// end session cookie
}
